import * as React from 'react'
import { useSelector } from 'react-redux'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import ExportDialog from '../ExportDialog'
import FilterUpgradeTooltip from '../filters/FilterUpgradeTooltip'
import { DEFAULT_FILTERS, type UsageFiltersState } from '../filters/types'
import { buildMockTeamMembersTableRows } from '../mockUpgradeData'
import { UpgradeCta } from '../UpgradeOverlay'

import TeamMembersFilters from './TeamMembersFilters'
import TeamMembersPagination from './TeamMembersPagination'
import TeamMembersTable from './TeamMembersTable'

import {
  type EnterpriseMembersTableResponse,
  type GetMembersTableSortByEnum,
  type GetMembersTableSortDirEnum,
  GetMembersTableWordsWindowEnum,
  getTeamMembersTable,
} from '@/api/teamMembersTable'
import { useAnalytics } from '@/hooks/useAnalytics'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'
import { SettingType } from '@/types'
import { EventName } from '@/utils/eventDefinitions'

const TEAM_MEMBERS_PAGE_SIZE = 25

const SEARCH_DEBOUNCE_MS = 300

type StatusValue = 'active' | 'trialing'

const renderEmptyState = (search: string, hasFilters: boolean): string => {
  const trimmed = search.trim()
  if (trimmed) return `No team members match “${trimmed}”.`
  if (hasFilters) return 'No team members match the current filters.'
  return 'No team members yet.'
}

interface TeamMembersTabProps {
  upgradeLocked?: boolean
}

const TeamMembersTab = ({ upgradeLocked = false }: TeamMembersTabProps) => {
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseId = enterprise?.id ?? null
  const canExportUsage = enterprise?.settings?.[SettingType.usageExportAllowed] !== false
  const showLastUsedAt = enterprise?.settings?.[SettingType.showLastUsedAt] === true
  const { trackEvent } = useAnalytics()
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [status, setStatus] = React.useState<StatusValue | undefined>(undefined)
  const [teamId, setTeamId] = React.useState<string | undefined>(undefined)
  const [sortBy, setSortBy] = React.useState<GetMembersTableSortByEnum>('totalWords')
  const [sortDir, setSortDir] = React.useState<GetMembersTableSortDirEnum>('desc')
  const [wordsWindow, setWordsWindow] = React.useState<GetMembersTableWordsWindowEnum>(
    GetMembersTableWordsWindowEnum.AllTime
  )
  const [page, setPage] = React.useState(0)

  // Debounce the search text so each keystroke doesn't fire a query.
  // The visible input stays responsive (controlled), only the server-bound
  // value lags.
  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [search])

  // Any filter / sort / search change invalidates the current page index.
  // Without this, page=4 in a filtered set may exceed the new ``total``.
  React.useEffect(() => {
    setPage(0)
  }, [debouncedSearch, status, teamId, sortBy, sortDir, wordsWindow])

  const queryParams = React.useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status,
      teamId,
      sortBy,
      sortDir,
      wordsWindow,
      offset: page * TEAM_MEMBERS_PAGE_SIZE,
      limit: TEAM_MEMBERS_PAGE_SIZE,
    }),
    [debouncedSearch, status, teamId, sortBy, sortDir, wordsWindow, page]
  )

  const tableQuery = useQuery<EnterpriseMembersTableResponse>({
    queryKey: ['team-members', 'table', enterpriseId, queryParams],
    queryFn: ({ signal }) => getTeamMembersTable({ ...queryParams, signal }).then((r) => r.data),
    placeholderData: keepPreviousData,
    enabled: !upgradeLocked && !!enterpriseId,
  })

  const rows = tableQuery.data?.rows ?? []
  const total = tableQuery.data?.total ?? 0
  const hasFilters = !!status || !!teamId

  // ExportDialog speaks the Overview UsageFiltersState shape. Only teamId overlaps with this tab's filters
  const exportInitialFilters: UsageFiltersState = React.useMemo(
    () => ({ ...DEFAULT_FILTERS, teamId: teamId ?? null }),
    [teamId]
  )

  if (upgradeLocked) {
    const mockRows = buildMockTeamMembersTableRows()
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <TeamMembersFilters
            search=""
            onSearchChange={() => {}}
            status={undefined}
            onStatusChange={() => {}}
            teamId={undefined}
            onTeamIdChange={() => {}}
            wordsWindow={GetMembersTableWordsWindowEnum.AllTime}
            onWordsWindowChange={() => {}}
            isFetching={false}
            upgradeLocked
          />
          {canExportUsage && (
            <FilterUpgradeTooltip active align="end">
              <ExportDialog enterpriseId={enterpriseId} initialFilters={DEFAULT_FILTERS} disabled />
            </FilterUpgradeTooltip>
          )}
        </div>
        <div className="relative">
          <div
            aria-hidden
            className="[&_[data-slot=table-body]]:pointer-events-none [&_[data-slot=table-body]]:select-none [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(1)]:blur-[1px] [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(2)]:blur-[2px] [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(3)]:blur-[3px] [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(4)]:blur-[4px] [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(5)]:blur-[5px] [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(6)]:blur-[6px] [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(7)]:blur-[7px] [&_[data-slot=table-body]_[data-slot=table-row]:nth-child(n+8)]:blur-[8px]"
          >
            <TeamMembersTable
              rows={mockRows}
              sortBy="totalWords"
              sortDir="desc"
              onSortChange={() => {}}
              isLoading={false}
              isError={false}
              emptyState=""
              showLastUsedAt={showLastUsedAt}
            />
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 top-12 flex items-center justify-center px-6">
            <UpgradeCta />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <TeamMembersFilters
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          teamId={teamId}
          onTeamIdChange={setTeamId}
          wordsWindow={wordsWindow}
          onWordsWindowChange={setWordsWindow}
          isFetching={tableQuery.isFetching || search !== debouncedSearch}
        />
        {canExportUsage && (
          <ExportDialog
            enterpriseId={enterpriseId}
            initialFilters={exportInitialFilters}
            disabled={!enterpriseId}
            onExport={() => trackEvent?.(EventName.TeamInsightsExported, { tab: 'team-members' })}
          />
        )}
      </div>
      <TeamMembersTable
        rows={rows}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={({ sortBy: nextSortBy, sortDir: nextSortDir }) => {
          setSortBy(nextSortBy)
          setSortDir(nextSortDir)
        }}
        isLoading={tableQuery.isLoading}
        isError={tableQuery.isError}
        emptyState={renderEmptyState(debouncedSearch, hasFilters)}
        showLastUsedAt={showLastUsedAt}
      />
      <TeamMembersPagination
        page={page}
        pageSize={TEAM_MEMBERS_PAGE_SIZE}
        total={total}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />
    </div>
  )
}

export default TeamMembersTab
